/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, GridLegacy, IconButton, Popover, TextField, Typography } from '@mui/material';
import { ProfileViewModel } from './ProfileViewModel';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ProfileViewStyles from './ProfileViewStyles';
import { FormattedMessage } from 'react-intl';
import { useViewModel } from '../../../hooks/useViewModel';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../../../navigation/Pages';

const ProfileView = observer((): ReactElement => {
	const viewModel: ProfileViewModel = useViewModel(ProfileViewModel);
	const { classes } = ProfileViewStyles();
	const navigate = useNavigate();

	const imgSrc = viewModel.getProfileImage();

	return (
		<>
			<Button variant='outlined' className={classes.profileButton} onClick={viewModel.openPopover}>
				<Typography variant='h2'>{viewModel.getUsername()}</Typography>
				<ArrowDropDownIcon color='info' fontWeight='bold' />
			</Button>
			<Popover
				open={viewModel.isOpen()}
				anchorEl={viewModel.getAnchorEl()}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				onClose={() => {
					viewModel.closePopover();
					viewModel.setIsInput(false);
				}}
				classes={{ paper: classes.popover }}>
				<GridLegacy item xs={12} gap={2} className={classes.container} height='80px' marginX='15px'>
					<GridLegacy
						item
						xs={4}
						className={classes.imageContainer}
						display='flex'
						justifyContent='flex-start'
						alignItems='center'>
						<IconButton className={classes.iconButton} component='label'>
							<img src={imgSrc} alt='Profile Image' className={classes.profileImage} />
							<img src='/img/editIcon.svg' alt='Edit Icon' className={classes.profileImageIcon} />
							<input
								type='file'
								hidden
								onChange={(event) => {
									viewModel.updateImage(event);
								}}
							/>
						</IconButton>
					</GridLegacy>
					<GridLegacy flexDirection='column' item className={classes.nameContainer}>
						{viewModel.model.isInput ? (
							<GridLegacy item className={classes.container}>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										viewModel.updateName();
										viewModel.setIsInput(false);
									}}>
									<TextField
										InputProps={{
											classes: {
												input: classes.input,
											},
										}}
										onChange={(event) => {
											viewModel.changeName(event);
										}}
										variant='standard'
										className={classes.textField}
										placeholder={viewModel.getUsername()}
										autoFocus={true}
									/>
									<Button type='submit' className={classes.submitButton} color='info'>
										<FormattedMessage
											id={viewModel.model.name === viewModel.getUsername() ? 'general.cancel' : 'general.change'}
										/>
									</Button>
								</form>
							</GridLegacy>
						) : (
							<GridLegacy item className={classes.container}>
								<Typography variant='h2' className={classes.username}>
									{viewModel.getUsername()}
								</Typography>
								<IconButton
									className={classes.editNameButton}
									onClick={() => {
										viewModel.setIsInput(true);
									}}>
									<img src='/img/editIcon.svg' alt='Edit Icon' className={classes.editNameButton} />
								</IconButton>
							</GridLegacy>
						)}
						<GridLegacy item className={classes.courseLeader}>
							<FormattedMessage id='profile.course_leader' />
						</GridLegacy>
					</GridLegacy>
				</GridLegacy>
				<Divider variant='middle' />
				<GridLegacy item xs={11} marginX='15px'>
					<Button
						onClick={() => {
							viewModel.logout();
							navigate(Pages.REQUEST_LOGIN);
						}}
						color='error'
						variant='text'
						className={`${classes.logout} ${classes.label}`}>
						<FormattedMessage id={'profile.logout'} />
					</Button>
				</GridLegacy>
			</Popover>
		</>
	);
});

export default ProfileView;
